import { apiClient } from './api';
import type { ApiResponse } from '@/types';
import type { ChannelType } from './channelsService';

export interface Template {
  id: string;
  tenant_id: string;
  channel: ChannelType;
  name: string;
  language: string;
  status: 'draft' | 'active' | 'archived' | 'pending' | 'approved' | 'rejected';
  provider_status?: 'APPROVED' | 'PENDING' | 'REJECTED' | string;
  content: {
    type?: 'text' | 'template';
    body?: string;
    header?: string;
    footer?: string;
    buttons?: Array<{ type: string; text: string }>;
    variables?: string[];
  };
  created_at: string;
  updated_at: string;
}

export interface ApprovedMetaTemplate {
  id: string;
  name: string;
  language: string;
  status: 'APPROVED';
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' | string;
  components: Array<{
    type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
    format?: string;
    text?: string;
    buttons?: Array<{ type: string; text: string }>;
  }>;
}

export interface ListTemplatesParams {
  channel?: ChannelType;
  status?: string;
}

export async function listTemplates(params?: ListTemplatesParams): Promise<ApiResponse<Template[]>> {
  return apiClient.get<Template[]>('/templates', params);
}

export async function listApprovedMetaTemplates(): Promise<ApiResponse<ApprovedMetaTemplate[]>> {
  return apiClient.get<ApprovedMetaTemplate[]>('/templates/meta/approved');
}

export async function getTemplate(id: string): Promise<ApiResponse<Template>> {
  return apiClient.get<Template>(`/templates/${id}`);
}

export interface CreateTemplateRequest {
  channel: ChannelType;
  name: string;
  language?: string;
  status?: Template['status'];
  content: Template['content'];
}

export async function createTemplate(payload: CreateTemplateRequest): Promise<ApiResponse<Template>> {
  return apiClient.post<Template>('/templates', payload);
}

export async function updateTemplate(id: string, payload: Partial<CreateTemplateRequest>): Promise<ApiResponse<Template>> {
  return apiClient.put<Template>(`/templates/${id}`, payload);
}

export async function deleteTemplate(id: string): Promise<ApiResponse<{ message: string }>> {
  return apiClient.delete<{ message: string }>(`/templates/${id}`);
}

// Legacy aliases for backward compatibility
export type ChannelTemplate = Template;
export const listChannelTemplates = listTemplates;
export const createChannelTemplate = createTemplate;

// Meta API Integration
export async function syncMetaTemplates(): Promise<ApiResponse<Template[]>> {
  return apiClient.get<Template[]>('/templates/meta/sync');
}

export interface CreateMetaTemplateRequest {
  name: string;
  language?: string;
  category?: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  header?: string;
  body: string;
  footer?: string;
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
    text: string;
    url?: string;
    phone_number?: string;
  }>;
}

export async function createMetaTemplate(data: CreateMetaTemplateRequest): Promise<ApiResponse<Template>> {
  return apiClient.post<Template>('/templates/meta/create', data);
}

export async function getMetaTemplateStatus(name: string): Promise<ApiResponse<any>> {
  return apiClient.get<any>(`/templates/meta/status/${name}`);
}
